/**
 * @module NotificationItem
 * @package @geenius/notifications-react-css
 * @description Renders a single inbox row for the React vanilla CSS variant.
 */

import type { Notification } from '@geenius/notifications/shared'
import {
  formatNotificationRelativeTime,
  getNotificationIcon,
} from '@geenius/notifications/shared'
import type { JSX } from 'react'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for a single notification row.
 */
export interface NotificationItemProps {
  notification: Notification
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  className?: string
}

/**
 * Renders a single notification row with optional read and dismiss actions.
 *
 * @param props Notification row props.
 * @returns The rendered notification row.
 */
export function NotificationItem({
  notification,
  onRead,
  onDismiss,
  className,
}: NotificationItemProps): JSX.Element {
  const { t } = useNotificationCopy()
  const isUnread = notification.status === 'unread'

  return (
    <article
      className={cx(
        'notifications__item',
        'gm-motion',
        'gm-motion--fade-in',
        isUnread ? 'notifications__item--unread' : 'notifications__item--read',
        className,
      )}
    >
      {notification.actorAvatar ? (
        <img
          className={cx('notifications__item-avatar')}
          src={notification.actorAvatar}
          alt=""
        />
      ) : (
        <span
          className={cx(
            'notifications__item-icon',
            `notifications__item-icon--${notification.type}`,
          )}
        >
          {getNotificationIcon(notification.type)}
        </span>
      )}
      <div className={cx('notifications__item-content')}>
        <button
          type="button"
          className={cx('notifications__item-trigger')}
          onClick={() => {
            if (isUnread) {
              onRead?.(notification.id)
            }
          }}
          disabled={!isUnread || !onRead}
          aria-label={
            isUnread
              ? t('notifications.item.markReadAria', { title: notification.title })
              : t('notifications.item.notificationAria', { title: notification.title })
          }
        >
          <div className={cx('notifications__item-header')}>
            <div>
              <div className={cx('notifications__item-title')}>{notification.title}</div>
              <div className={cx('notifications__item-body')}>{notification.body}</div>
            </div>
            {isUnread ? <span className={cx('notifications__item-unread-dot')} /> : null}
          </div>
        </button>
        <div className={cx('notifications__item-footer')}>
          <span className={cx('notifications__item-time')}>
            {formatNotificationRelativeTime(notification.createdAt, t)}
          </span>
          {notification.actionUrl ? (
            <a className={cx('notifications__item-action')} href={notification.actionUrl}>
              {notification.actionLabel ?? t('notifications.common.view')}
            </a>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              className={cx('notifications__item-dismiss')}
              onClick={() => onDismiss(notification.id)}
            >
              {t('notifications.common.dismiss')}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  )
}
